import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { statusTone } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Clock, MapPin, FileSignature, DollarSign, Utensils, CheckSquare } from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks, parseISO,
} from "date-fns";

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month");
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const eventsOn = (day) => events.filter((e) => {
    if (!e.event_date) return false;
    try { return isSameDay(parseISO(e.event_date), day); } catch { return false; }
  });

  const today = new Date();
  const monthStart = startOfMonth(cursor);
  const monthDays = eachDayOfInterval({ start: startOfWeek(monthStart), end: endOfWeek(endOfMonth(cursor)) });
  const weekDays = eachDayOfInterval({ start: startOfWeek(cursor), end: endOfWeek(cursor) });

  const prev = () => setCursor(view === "month" ? subMonths(cursor, 1) : subWeeks(cursor, 1));
  const next = () => setCursor(view === "month" ? addMonths(cursor, 1) : addWeeks(cursor, 1));

  const upcoming = events
    .filter((e) => e.event_date && parseISO(e.event_date) >= new Date(today.toDateString()) && e.event_status !== "Cancelled")
    .sort((a, b) => a.event_date.localeCompare(b.event_date))
    .slice(0, 8);

  const headerLabel = view === "month"
    ? format(cursor, "MMMM yyyy")
    : `${format(weekDays[0], "MMM d")} – ${format(weekDays[6], "MMM d, yyyy")}`;

  return (
    <div className="animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div><p className="eyebrow mb-2">Schedule</p><h1 className="font-serif text-4xl">Calendar</h1></div>
        <div className="flex items-center gap-1 bg-clay rounded-full p-1">
          {["month", "week"].map((v) => (
            <button key={v} onClick={() => setView(v)} className={`px-4 py-1.5 rounded-full text-sm capitalize transition-colors ${view === v ? "bg-ink text-white" : "text-ink/70"}`} data-testid={`cal-view-${v}`}>{v}</button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 df-card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl">{headerLabel}</h2>
            <div className="flex gap-2">
              <button onClick={prev} className="p-2 rounded-full hover:bg-clay" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => setCursor(new Date())} className="px-3 rounded-full hover:bg-clay text-sm">Today</button>
              <button onClick={next} className="p-2 rounded-full hover:bg-clay" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>

          {view === "month" ? (
            <>
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {monthDays.map((day) => {
                  const evs = eventsOn(day);
                  const inMonth = isSameMonth(day, cursor);
                  const isToday = isSameDay(day, today);
                  return (
                    <div key={day.toISOString()} className={`min-h-[72px] rounded-lg border p-1.5 text-left ${inMonth ? "bg-white border-line" : "bg-transparent border-transparent opacity-40"}`}>
                      <div className={`text-xs mb-1 ${isToday ? "font-bold text-olive" : "text-muted-foreground"}`}>{format(day, "d")}</div>
                      <div className="space-y-0.5">
                        {evs.slice(0, 2).map((e) => (
                          <button key={e.id} onClick={() => setSelected(e)} className="w-full truncate text-[10px] px-1 py-0.5 rounded bg-ink text-white text-left" data-testid={`cal-event-${e.id}`}>{e.full_name}</button>
                        ))}
                        {evs.length > 2 && <div className="text-[9px] text-muted-foreground pl-1">+{evs.length - 2} more</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="week-view">
              {weekDays.map((day) => {
                const evs = eventsOn(day);
                const isToday = isSameDay(day, today);
                return (
                  <div key={day.toISOString()} className={`rounded-xl border p-2 min-h-[220px] ${isToday ? "border-olive bg-olive/5" : "border-line bg-white"}`}>
                    <div className="text-center mb-2 pb-2 border-b border-line">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{format(day, "EEE")}</div>
                      <div className={`font-serif text-xl ${isToday ? "text-olive" : ""}`}>{format(day, "d")}</div>
                    </div>
                    <div className="space-y-2">
                      {evs.length === 0 && <div className="text-[10px] text-muted-foreground text-center pt-4">—</div>}
                      {evs.map((e) => (
                        <button key={e.id} onClick={() => setSelected(e)} className="w-full text-left rounded-lg bg-clay p-2 hover:bg-sand transition-colors" data-testid={`week-event-${e.id}`}>
                          <div className="font-medium text-xs truncate">{e.full_name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{e.event_city}</div>
                          <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> Setup {e.setup_arrival_time || "—"}</div>
                          <div className="text-[10px] text-muted-foreground">Service {e.service_start_time || "—"}–{e.service_end_time || "—"}</div>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            <span className={`text-[8px] px-1.5 py-0.5 rounded-full border ${statusTone(e.payment_status)}`}>{e.payment_status}</span>
                            <span className={`text-[8px] px-1.5 py-0.5 rounded-full border ${statusTone(e.contract_status)}`}>{e.contract_status}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="df-card p-6">
          <h2 className="font-serif text-2xl mb-4">Upcoming</h2>
          <div className="space-y-2">
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nothing upcoming.</p>}
            {upcoming.map((e) => (
              <button key={e.id} onClick={() => setSelected(e)} className="w-full text-left p-3 rounded-xl hover:bg-clay transition-colors" data-testid={`upcoming-${e.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium truncate">{e.full_name}</span>
                  <span className="text-xs text-muted-foreground">{format(parseISO(e.event_date), "MMM d")}</span>
                </div>
                <div className="text-xs text-muted-foreground">{e.event_type} · {e.event_city}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => setSelected(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div><h3 className="font-serif text-2xl">{selected.full_name}</h3><p className="text-sm text-muted-foreground">{selected.event_type} · {selected.event_date}</p></div>
              <Badge variant="outline" className={statusTone(selected.event_status)}>{selected.event_status}</Badge>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground mb-5">
              <div className="flex items-center gap-2"><Clock className="h-4 w-4" /> Setup {selected.setup_arrival_time || "—"} · Service {selected.service_start_time || "—"}–{selected.service_end_time || "—"}</div>
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {selected.event_city} {selected.event_address ? `· ${selected.event_address}` : ""}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-5">
              <Ind icon={FileSignature} label="Contract" value={selected.contract_status} />
              <Ind icon={DollarSign} label="Payment" value={selected.payment_status} />
              <Ind icon={Utensils} label="Menu" value={selected.menu_status} />
              <Ind icon={CheckSquare} label="Supplies" value={selected.supplies_status} />
            </div>
            <button onClick={() => navigate(`/admin/events/${selected.id}`)} className="btn-dark w-full" data-testid="cal-open-event">Open Event</button>
          </div>
        </div>
      )}
    </div>
  );
}

const Ind = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-xl bg-clay px-3 py-2">
    <Icon className="h-4 w-4 text-olive shrink-0" />
    <div className="min-w-0"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="text-xs truncate">{value || "—"}</div></div>
  </div>
);
